import { Fragment, useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  AlertTriangle, ChevronRight, CircleCheck, ClipboardList, History, Loader2,
  PackageCheck, PackagePlus, Plus, Send, Settings2, Trash2, X,
} from "lucide-react";
import { api, apiError, fmt } from "@/lib/api";

const INPUT = "h-10 w-full rounded-lg border border-white/10 bg-black/40 px-3 text-sm text-white placeholder:text-neutral-600 focus:border-amber-500/60 focus:outline-none";

const PO_STATUS = {
  draft: { label: "Draft", cls: "border-white/20 bg-white/5 text-neutral-300" },
  sent: { label: "Sent", cls: "border-sky-500/30 bg-sky-500/10 text-sky-300" },
  partially_received: { label: "Partially Received", cls: "border-amber-500/30 bg-amber-500/10 text-amber-300" },
  received: { label: "Received", cls: "border-emerald-500/30 bg-emerald-500/10 text-emerald-400" },
  closed: { label: "Closed", cls: "border-white/10 bg-white/[0.03] text-neutral-500" },
};

const ROW_STATUS = {
  out: { label: "OUT", cls: "border-red-500/40 bg-red-500/10 text-red-400" },
  low: { label: "LOW", cls: "border-amber-500/40 bg-amber-500/10 text-amber-300" },
  ok: { label: "OK", cls: "border-emerald-500/30 bg-emerald-500/10 text-emerald-400" },
};

const StatusPill = ({ status, testid }) => (
  <span data-testid={testid} className={`inline-block rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${PO_STATUS[status]?.cls || ""}`}>
    {PO_STATUS[status]?.label || status}
  </span>
);

const RowPill = ({ status, testid }) => (
  <span data-testid={testid} className={`inline-block rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${ROW_STATUS[status].cls}`}>
    {ROW_STATUS[status].label}
  </span>
);

const Modal = ({ title, sub, onClose, children, testid, wide }) => (
  <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm" data-testid={testid}>
    <div className={`max-h-[90vh] w-full overflow-y-auto rounded-2xl border border-white/10 bg-[#F1EDE3] p-6 ${wide ? "max-w-3xl" : "max-w-md"}`} data-lenis-prevent>
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <h3 className="text-lg font-bold text-white">{title}</h3>
          {sub && <p className="mt-0.5 text-xs text-neutral-500">{sub}</p>}
        </div>
        <button onClick={onClose} aria-label="Close" data-testid={`${testid}-close`}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-white/10 text-neutral-400 hover:text-white">
          <X className="h-4 w-4" />
        </button>
      </div>
      {children}
    </div>
  </div>
);

/* ---------------- Replenishment settings + cost history ---------------- */

function SettingsModal({ row, suppliers, onClose, onSaved }) {
  const [form, setForm] = useState({
    min_stock: row.min_stock, reorder_level: row.reorder_level,
    reorder_qty: row.reorder_qty, preferred_supplier_id: row.preferred_supplier_id || "",
    notes: "",
  });
  const [history, setHistory] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/admin/procurement/cost-history?sku=${encodeURIComponent(row.sku)}`)
      .then(({ data }) => setHistory(data.history))
      .catch(() => setHistory([]));
  }, [row.sku]);

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/procurement/replenishment/${encodeURIComponent(row.sku)}`, {
        product_id: row.product_id,
        min_stock: parseInt(form.min_stock) || 0,
        reorder_level: parseInt(form.reorder_level) || 0,
        reorder_qty: parseInt(form.reorder_qty) || 0,
        preferred_supplier_id: form.preferred_supplier_id || null,
        notes: form.notes,
      });
      toast.success(`Replenishment settings saved for ${row.sku}`);
      onSaved();
    } catch (e) {
      toast.error(apiError(e, "Couldn't save settings"));
    } finally {
      setBusy(false);
    }
  };

  const num = (k, label, testid, hint) => (
    <div>
      <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-neutral-500">{label}</label>
      <input type="number" min="0" value={form[k]} data-testid={testid}
        onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))} className={INPUT} />
      {hint && <p className="mt-1 text-[11px] text-neutral-600">{hint}</p>}
    </div>
  );

  return (
    <Modal testid="settings-modal" onClose={onClose} wide
      title={`Replenishment · ${row.sku}`}
      sub={`${row.product_name}${row.variant_label ? ` · ${row.variant_label}` : ""} — available ${row.available} (${row.stock} in stock, ${row.reserved} reserved)`}>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {num("min_stock", "Minimum stock", "settings-min", "Safety floor for this SKU.")}
        {num("reorder_level", "Reorder level", "settings-reorder-level", "Alert triggers at or below this available stock.")}
        {num("reorder_qty", "Reorder qty", "settings-reorder-qty", "0 = suggest topping up to 2× reorder level.")}
      </div>
      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-neutral-500">Preferred supplier</label>
          <select value={form.preferred_supplier_id} data-testid="settings-supplier"
            onChange={(e) => setForm((f) => ({ ...f, preferred_supplier_id: e.target.value }))} className={INPUT}>
            <option value="">None</option>
            {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-neutral-500">Notes</label>
          <input value={form.notes} data-testid="settings-notes"
            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} className={INPUT} placeholder="Optional" />
        </div>
      </div>

      <div className="mt-5 border-t border-white/[0.07] pt-4">
        <p className="mb-2 flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-neutral-500">
          <History className="h-3.5 w-3.5" /> Supplier purchase-cost history
        </p>
        {!history ? (
          <Loader2 className="h-4 w-4 animate-spin text-neutral-600" />
        ) : history.length === 0 ? (
          <p className="text-xs text-neutral-600" data-testid="settings-cost-history-empty">No purchases recorded for this SKU yet.</p>
        ) : (
          <div className="max-h-44 space-y-1.5 overflow-y-auto" data-testid="settings-cost-history" data-lenis-prevent>
            {history.map((h, i) => (
              <p key={i} className="text-xs text-neutral-400">
                <span className="font-mono text-amber-400">{h.purchase_number}</span> · {h.supplier_name} · {h.purchase_date} ·{" "}
                {h.qty} pcs @ <strong className="text-white">{fmt(h.unit_cost)}</strong>
                {h.gst_rate ? ` · GST ${h.gst_rate}%` : ""} · {h.status}
              </p>
            ))}
          </div>
        )}
      </div>

      <button onClick={save} disabled={busy} data-testid="settings-save"
        className="mt-5 flex w-full items-center justify-center gap-2 rounded-xl bg-amber-500 py-3 text-sm font-bold text-black transition-colors hover:bg-amber-400 disabled:opacity-50">
        {busy && <Loader2 className="h-4 w-4 animate-spin" />} Save settings
      </button>
    </Modal>
  );
}

/* ---------------- PO create form (manual or from a recommendation) ---------------- */

function PoFormModal({ suppliers, products, prefill, onClose, onCreated }) {
  const locked = prefill.source === "recommendation";
  const [supplierId, setSupplierId] = useState(prefill.supplier_id || "");
  const [items, setItems] = useState(prefill.items);
  const [expected, setExpected] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const setItem = (i, k, v) => setItems((rows) => rows.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  const variantsOf = (pid) => products.find((p) => p.id === pid)?.variants?.filter((v) => v.enabled) || [];
  const subtotal = items.reduce((n, i) => n + (parseInt(i.qty) || 0) * (parseFloat(i.unit_cost) || 0), 0);
  const tax = items.reduce((n, i) => n + (parseInt(i.qty) || 0) * (parseFloat(i.unit_cost) || 0) * (parseFloat(i.gst_rate) || 0) / 100, 0);

  const submit = async () => {
    if (!supplierId) { toast.error("Select a supplier"); return; }
    const clean = items.filter((i) => i.product_id && i.sku && parseInt(i.qty) > 0 && i.unit_cost !== "");
    if (!clean.length) { toast.error("Add at least one complete item row"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/procurement/pos", {
        supplier_id: supplierId, expected_delivery_date: expected, notes, source: prefill.source,
        items: clean.map((i) => ({ product_id: i.product_id, sku: i.sku, qty: parseInt(i.qty),
                                   unit_cost: parseFloat(i.unit_cost), gst_rate: parseFloat(i.gst_rate) || 0 })),
      });
      toast.success(`Purchase order ${data.po_number} created as draft`);
      onCreated();
    } catch (e) {
      toast.error(apiError(e, "Couldn't create the purchase order"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal testid="po-form-modal" onClose={onClose} wide
      title={locked ? "Create PO from recommendation" : "New purchase order"}
      sub={locked ? "Prefilled from the replenishment recommendation — adjust qty/cost if needed." : "Draft first — mark sent, then receive stock against it."}>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <select value={supplierId} onChange={(e) => setSupplierId(e.target.value)} data-testid="po-supplier" className={INPUT}>
          <option value="">Select supplier…</option>
          {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <input type="date" value={expected} onChange={(e) => setExpected(e.target.value)} data-testid="po-expected" className={INPUT} title="Expected delivery date" />
        <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes" data-testid="po-notes" className={INPUT} />
      </div>

      <div className="mt-4 space-y-2">
        {items.map((it, i) => (
          <div key={i} className="grid grid-cols-2 gap-2 sm:grid-cols-[1fr_1fr_80px_110px_80px_36px]" data-testid={`po-item-${i}`}>
            {locked ? (
              <p className="col-span-2 flex items-center text-xs text-neutral-300 sm:col-span-2">
                {it.product_name} <span className="ml-2 font-mono text-amber-400">{it.sku}</span>
              </p>
            ) : (
              <Fragment>
                <select value={it.product_id} onChange={(e) => setItem(i, "product_id", e.target.value) || setItem(i, "sku", "")}
                  data-testid={`po-product-${i}`} className={INPUT}>
                  <option value="">Product…</option>
                  {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
                <select value={it.sku} onChange={(e) => setItem(i, "sku", e.target.value)} data-testid={`po-variant-${i}`} className={INPUT}>
                  <option value="">Variant…</option>
                  {variantsOf(it.product_id).map((v) => <option key={v.sku} value={v.sku}>{v.label} ({v.sku})</option>)}
                </select>
              </Fragment>
            )}
            <input type="number" min="1" value={it.qty} onChange={(e) => setItem(i, "qty", e.target.value)} placeholder="Qty" data-testid={`po-qty-${i}`} className={INPUT} />
            <input type="number" min="0" step="0.01" value={it.unit_cost} onChange={(e) => setItem(i, "unit_cost", e.target.value)} placeholder="Cost/pc ₹" data-testid={`po-cost-${i}`} className={INPUT} />
            <input type="number" min="0" max="28" value={it.gst_rate} onChange={(e) => setItem(i, "gst_rate", e.target.value)} placeholder="GST%" data-testid={`po-gst-${i}`} className={INPUT} />
            {!locked && (
              <button onClick={() => setItems((rows) => rows.filter((_, idx) => idx !== i))} aria-label="Remove row"
                data-testid={`po-remove-${i}`} className="flex h-10 items-center justify-center rounded-lg border border-white/10 text-neutral-500 hover:text-red-300">
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </div>
        ))}
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        {!locked ? (
          <button onClick={() => setItems((rows) => [...rows, { product_id: "", sku: "", qty: 1, unit_cost: "", gst_rate: 18 }])}
            data-testid="po-add-row"
            className="flex h-9 items-center gap-1.5 rounded-full border border-white/15 px-4 text-xs font-semibold text-neutral-300 hover:text-white">
            <Plus className="h-3.5 w-3.5" /> Add item
          </button>
        ) : <span />}
        <div className="flex items-center gap-4 text-xs text-neutral-400">
          <span>Subtotal <strong className="text-white">{fmt(subtotal)}</strong> · GST <strong className="text-white">{fmt(tax)}</strong> · Total <strong className="text-amber-300">{fmt(subtotal + tax)}</strong></span>
          <button onClick={submit} disabled={busy} data-testid="po-submit"
            className="flex h-10 items-center gap-2 rounded-full bg-amber-500 px-5 text-xs font-bold text-black hover:bg-amber-400 disabled:opacity-40">
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Create draft PO
          </button>
        </div>
      </div>
    </Modal>
  );
}

/* ---------------- Replenishment tab ---------------- */

function ReplenishmentTab({ suppliers, onCreatePo, onOpenPo }) {
  const [data, setData] = useState(null);
  const [lowOnly, setLowOnly] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/procurement/replenishment")
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(apiError(e, "Couldn't load replenishment data")));
  }, []);
  useEffect(load, [load]);

  if (!data) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-neutral-600" /></div>;
  const rows = lowOnly ? data.rows.filter((r) => r.status !== "ok") : data.rows;
  const s = data.summary;

  const actionButtons = (r, compact) => (
    <span className={`flex items-center gap-2 ${compact ? "" : "justify-end"}`}>
      <button onClick={() => setEditing(r)} data-testid={`repl-settings-${r.sku}`}
        className="flex h-8 items-center gap-1.5 rounded-full border border-white/15 px-3 text-[11px] font-bold text-neutral-300 hover:text-white">
        <Settings2 className="h-3.5 w-3.5" /> Settings
      </button>
      {r.open_po ? (
        <button onClick={() => onOpenPo(r.open_po)} data-testid={`repl-openpo-${r.sku}`}
          className="flex h-8 items-center gap-1.5 rounded-full border border-sky-500/40 bg-sky-500/10 px-3 text-[11px] font-bold text-sky-300 hover:bg-sky-500/20">
          <ClipboardList className="h-3.5 w-3.5" /> {r.open_po.po_number} open
        </button>
      ) : (
        <button onClick={() => onCreatePo(r)} data-testid={`repl-create-po-${r.sku}`}
          className="flex h-8 items-center gap-1.5 rounded-full border border-amber-500/40 bg-amber-500/10 px-3 text-[11px] font-bold text-amber-300 hover:bg-amber-500/20">
          <PackagePlus className="h-3.5 w-3.5" /> Create PO
        </button>
      )}
    </span>
  );

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-2" data-testid="repl-summary">
        <span className="rounded-full border border-red-500/30 bg-red-500/10 px-3 py-1.5 text-xs font-bold text-red-400">{s.out} out of stock</span>
        <span className="rounded-full border border-amber-500/30 bg-amber-500/10 px-3 py-1.5 text-xs font-bold text-amber-300">{s.low} at/below reorder level</span>
        <span className="rounded-full border border-sky-500/30 bg-sky-500/10 px-3 py-1.5 text-xs font-bold text-sky-300">{s.open_pos} open POs</span>
        <span className={`rounded-full border px-3 py-1.5 text-xs font-bold ${s.overdue_pos ? "border-red-500/30 bg-red-500/10 text-red-400" : "border-white/10 bg-white/[0.03] text-neutral-500"}`}>
          {s.overdue_pos} overdue deliveries
        </span>
        <label className="ml-auto flex cursor-pointer items-center gap-2 text-xs text-neutral-400">
          <input type="checkbox" checked={lowOnly} onChange={(e) => setLowOnly(e.target.checked)} data-testid="repl-filter-low" className="accent-amber-500" />
          Needs attention only
        </label>
      </div>

      {rows.length === 0 ? (
        <div className="rounded-2xl border border-white/[0.07] bg-white/[0.02] py-16 text-center text-sm text-neutral-500" data-testid="repl-empty">
          Nothing needs replenishment right now.
        </div>
      ) : (
        <Fragment>
          <div className="hidden overflow-x-auto rounded-2xl border border-white/[0.07] md:block">
            <table className="w-full text-left text-sm" data-testid="repl-table">
              <thead>
                <tr className="border-b border-white/[0.07] bg-white/[0.03] text-[11px] uppercase tracking-wider text-neutral-500">
                  <th className="px-3 py-3">Status</th><th className="px-3 py-3">SKU</th><th className="px-3 py-3">Product</th>
                  <th className="px-3 py-3 text-right">Stock</th><th className="px-3 py-3 text-right">Resv.</th>
                  <th className="px-3 py-3 text-right">Avail.</th><th className="px-3 py-3 text-right">Min</th>
                  <th className="px-3 py-3 text-right">Reorder</th><th className="px-3 py-3 text-right">Suggest</th>
                  <th className="px-3 py-3">Preferred supplier</th><th className="px-3 py-3 text-right">Last cost</th>
                  <th className="px-3 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.sku} className="border-b border-white/[0.05] hover:bg-white/[0.02]" data-testid={`repl-row-${r.sku}`}>
                    <td className="px-3 py-3"><RowPill status={r.status} testid={`repl-status-${r.sku}`} /></td>
                    <td className="px-3 py-3 font-mono text-xs font-bold text-amber-400">{r.sku}</td>
                    <td className="max-w-[140px] truncate px-3 py-3 text-neutral-300" title={r.product_name}>
                      {r.product_name}{r.variant_label ? <span className="text-neutral-600"> · {r.variant_label}</span> : ""}
                    </td>
                    <td className="px-3 py-3 text-right text-neutral-300">{r.stock}</td>
                    <td className="px-3 py-3 text-right text-neutral-500">{r.reserved}</td>
                    <td className={`px-3 py-3 text-right font-bold ${r.available <= 0 ? "text-red-400" : "text-white"}`} data-testid={`repl-available-${r.sku}`}>{r.available}</td>
                    <td className="px-3 py-3 text-right text-neutral-500">{r.configured ? r.min_stock : "—"}</td>
                    <td className="px-3 py-3 text-right text-neutral-500">{r.configured ? r.reorder_level : "—"}</td>
                    <td className="px-3 py-3 text-right font-bold text-amber-300" data-testid={`repl-suggested-${r.sku}`}>{r.suggested_qty || "—"}</td>
                    <td className="px-3 py-3 text-xs text-neutral-400">{r.preferred_supplier_name || "—"}</td>
                    <td className="px-3 py-3 text-right text-xs text-neutral-400" data-testid={`repl-lastcost-${r.sku}`}>
                      {r.latest_cost ? fmt(r.latest_cost.unit_cost) : "—"}
                    </td>
                    <td className="px-3 py-3">{actionButtons(r)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="space-y-3 md:hidden" data-testid="repl-cards">
            {rows.map((r) => (
              <div key={r.sku} className="rounded-2xl border border-white/[0.07] bg-white/[0.02] p-4" data-testid={`repl-card-${r.sku}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-bold text-white">{r.product_name}</p>
                    <p className="font-mono text-xs text-amber-400">{r.sku}{r.variant_label ? ` · ${r.variant_label}` : ""}</p>
                  </div>
                  <RowPill status={r.status} testid={`repl-card-status-${r.sku}`} />
                </div>
                <div className="mt-3 grid grid-cols-4 gap-2 text-center text-xs">
                  {[["Stock", r.stock], ["Reserved", r.reserved], ["Available", r.available], ["Suggested", r.suggested_qty || "—"]].map(([l, v]) => (
                    <div key={l} className="rounded-lg bg-black/40 py-2">
                      <p className="text-[10px] uppercase tracking-wider text-neutral-600">{l}</p>
                      <p className="font-bold text-white">{v}</p>
                    </div>
                  ))}
                </div>
                <p className="mt-2 text-xs text-neutral-500">
                  {r.configured ? `Min ${r.min_stock} · Reorder lvl ${r.reorder_level}` : "Not configured"}
                  {r.preferred_supplier_name ? ` · ${r.preferred_supplier_name}` : ""}
                  {r.latest_cost ? ` · last cost ${fmt(r.latest_cost.unit_cost)}` : ""}
                </p>
                <div className="mt-3">{actionButtons(r, true)}</div>
              </div>
            ))}
          </div>
        </Fragment>
      )}

      {editing && (
        <SettingsModal row={editing} suppliers={suppliers}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}

/* ---------------- Receive modal ---------------- */

function ReceiveModal({ po, onClose, onReceived }) {
  const openLines = po.items.filter((i) => i.qty - (i.received_qty || 0) > 0);
  const [qtys, setQtys] = useState(Object.fromEntries(openLines.map((i) => [i.sku, i.qty - (i.received_qty || 0)])));
  const [notes, setNotes] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [ref, setRef] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const items = openLines.map((l) => ({ sku: l.sku, qty: parseInt(qtys[l.sku]) || 0 })).filter((l) => l.qty > 0);
    if (!items.length) { toast.error("Enter at least one quantity to receive"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/procurement/pos/${po.id}/receive`, {
        items, notes, purchase_date: date, reference_invoice: ref,
      });
      toast.success(data.status === "received"
        ? `${po.po_number} fully received — stock updated`
        : `Partial receipt recorded — ${data.units_total - data.units_received} unit(s) still open`);
      onReceived();
    } catch (e) {
      toast.error(apiError(e, "Couldn't receive stock"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal testid="receive-modal" onClose={onClose}
      title={`Receive stock · ${po.po_number}`}
      sub="Creates a receipt in Purchases and adds the stock to inventory.">
      <div className="space-y-2">
        {openLines.map((l) => {
          const remaining = l.qty - (l.received_qty || 0);
          return (
            <div key={l.sku} className="flex items-center gap-3" data-testid={`receive-line-${l.sku}`}>
              <div className="min-w-0 flex-1">
                <p className="truncate text-xs text-neutral-300">{l.product_name}</p>
                <p className="font-mono text-[11px] text-amber-400">{l.sku} · remaining {remaining}</p>
              </div>
              <input type="number" min="0" max={remaining} value={qtys[l.sku]}
                onChange={(e) => setQtys((q) => ({ ...q, [l.sku]: e.target.value }))}
                data-testid={`receive-qty-${l.sku}`} className={`${INPUT} w-24 text-right`} />
            </div>
          );
        })}
      </div>
      <div className="mt-4 grid grid-cols-2 gap-3">
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="receive-date" className={INPUT} title="Receipt date" />
        <input value={ref} onChange={(e) => setRef(e.target.value)} placeholder="Supplier invoice ref" data-testid="receive-ref" className={INPUT} />
      </div>
      <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes (optional)" data-testid="receive-notes" className={`${INPUT} mt-3`} />
      <button onClick={submit} disabled={busy} data-testid="receive-submit"
        className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-500 py-3 text-sm font-bold text-black transition-colors hover:bg-emerald-400 disabled:opacity-50">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <PackageCheck className="h-4 w-4" />} Receive into inventory
      </button>
    </Modal>
  );
}

/* ---------------- PO detail ---------------- */

function PoDetailModal({ po, onClose, onChanged }) {
  const [receiving, setReceiving] = useState(false);
  const [armClose, setArmClose] = useState(false);
  const [busy, setBusy] = useState(false);

  const act = async (fn, ok) => {
    setBusy(true);
    try {
      await fn();
      toast.success(ok);
      onChanged();
    } catch (e) {
      toast.error(apiError(e, "Action failed"));
    } finally {
      setBusy(false);
      setArmClose(false);
    }
  };

  return (
    <Modal testid="po-detail-modal" onClose={onClose} wide
      title={<span className="font-mono text-amber-400">{po.po_number}</span>}
      sub={`${po.supplier_name} · created ${po.created_at.slice(0, 10)} by ${po.created_by}${po.expected_delivery_date ? ` · expected ${po.expected_delivery_date}` : ""}`}>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <StatusPill status={po.status} testid="po-detail-status" />
        {po.overdue && (
          <span className="flex items-center gap-1 rounded-full border border-red-500/40 bg-red-500/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-red-400" data-testid="po-detail-overdue">
            <AlertTriangle className="h-3 w-3" /> Overdue
          </span>
        )}
        <span className="text-xs text-neutral-500">{po.units_received}/{po.units_total} units received · total {fmt(po.total)}</span>
      </div>

      <div className="overflow-x-auto rounded-xl border border-white/[0.07]">
        <table className="w-full min-w-[520px] text-left text-xs" data-testid="po-detail-items">
          <thead>
            <tr className="border-b border-white/[0.07] bg-white/[0.03] text-[10px] uppercase tracking-wider text-neutral-500">
              <th className="px-3 py-2">Item</th><th className="px-3 py-2 text-right">Ordered</th>
              <th className="px-3 py-2 text-right">Received</th><th className="px-3 py-2 text-right">Remaining</th>
              <th className="px-3 py-2 text-right">Unit cost</th><th className="px-3 py-2 text-right">Line total</th>
            </tr>
          </thead>
          <tbody>
            {po.items.map((i) => (
              <tr key={i.sku} className="border-b border-white/[0.05]">
                <td className="px-3 py-2 text-neutral-300">{i.product_name} <span className="font-mono text-amber-400">{i.sku}</span></td>
                <td className="px-3 py-2 text-right text-neutral-300">{i.qty}</td>
                <td className="px-3 py-2 text-right text-emerald-400">{i.received_qty || 0}</td>
                <td className="px-3 py-2 text-right font-bold text-white">{i.qty - (i.received_qty || 0)}</td>
                <td className="px-3 py-2 text-right text-neutral-400">{fmt(i.unit_cost)}{i.gst_rate ? ` +${i.gst_rate}%` : ""}</td>
                <td className="px-3 py-2 text-right text-neutral-300">{fmt(i.qty * i.unit_cost)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-2 text-[11px] text-neutral-600">Subtotal {fmt(po.subtotal)} + GST {fmt(po.tax)} = <strong className="text-amber-300">{fmt(po.total)}</strong>{po.notes ? ` · ${po.notes}` : ""}</p>

      {po.receipts.length > 0 && (
        <div className="mt-4" data-testid="po-receipts">
          <p className="mb-1.5 text-[10px] font-bold uppercase tracking-wider text-neutral-500">Receipts</p>
          {po.receipts.map((r) => (
            <p key={r.id} className="text-xs text-neutral-400">
              <span className="font-mono text-emerald-400">{r.purchase_number}</span> · {r.received_at.slice(0, 10)} ·{" "}
              {r.items.map((i) => `${i.sku} ×${i.qty}`).join(", ")} · by {r.received_by}
            </p>
          ))}
        </div>
      )}

      <div className="mt-4" data-testid="po-history">
        <p className="mb-1.5 text-[10px] font-bold uppercase tracking-wider text-neutral-500">Audit history</p>
        {po.history.map((h, i) => (
          <p key={i} className="text-xs text-neutral-400">
            <span className="text-neutral-600">{h.at.slice(0, 16).replace("T", " ")}</span> ·{" "}
            <strong className="text-neutral-300">{h.action}</strong> · {h.actor}{h.details ? ` — ${h.details}` : ""}
          </p>
        ))}
      </div>

      <div className="mt-5 flex flex-wrap gap-2">
        {po.status === "draft" && (
          <button onClick={() => act(() => api.post(`/admin/procurement/pos/${po.id}/send`), `${po.po_number} marked as sent`)}
            disabled={busy} data-testid="po-detail-send"
            className="flex h-10 items-center gap-2 rounded-full bg-sky-500 px-5 text-xs font-bold text-black hover:bg-sky-400 disabled:opacity-40">
            <Send className="h-3.5 w-3.5" /> Mark sent to supplier
          </button>
        )}
        {(po.status === "sent" || po.status === "partially_received") && (
          <button onClick={() => setReceiving(true)} data-testid="po-detail-receive"
            className="flex h-10 items-center gap-2 rounded-full bg-emerald-500 px-5 text-xs font-bold text-black hover:bg-emerald-400">
            <PackageCheck className="h-3.5 w-3.5" /> Receive stock
          </button>
        )}
        {po.status !== "closed" && (
          armClose ? (
            <button onClick={() => act(() => api.post(`/admin/procurement/pos/${po.id}/close`, { notes: "" }), `${po.po_number} closed`)}
              disabled={busy} data-testid="po-detail-close-confirm"
              className="flex h-10 items-center gap-2 rounded-full bg-red-500 px-5 text-xs font-bold text-black hover:bg-red-400 disabled:opacity-40">
              {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Confirm close{po.units_total - po.units_received > 0 ? ` (${po.units_total - po.units_received} unreceived)` : ""}
            </button>
          ) : (
            <button onClick={() => setArmClose(true)} data-testid="po-detail-close"
              className="flex h-10 items-center gap-2 rounded-full border border-red-500/40 px-5 text-xs font-bold text-red-300 hover:bg-red-500/10">
              <CircleCheck className="h-3.5 w-3.5" /> Close PO
            </button>
          )
        )}
      </div>

      {receiving && (
        <ReceiveModal po={po} onClose={() => setReceiving(false)}
          onReceived={() => { setReceiving(false); onChanged(); }} />
      )}
    </Modal>
  );
}

/* ---------------- Purchase orders tab ---------------- */

const PO_FILTERS = [
  { id: "", label: "All" }, { id: "open", label: "Open" }, { id: "draft", label: "Draft" },
  { id: "sent", label: "Sent" }, { id: "partially_received", label: "Partially Received" },
  { id: "received", label: "Received" }, { id: "closed", label: "Closed" }, { id: "overdue", label: "Overdue" },
];

function PurchaseOrdersTab({ onNewPo, reloadKey, openPoId, onOpenedPo }) {
  const [pos, setPos] = useState(null);
  const [filter, setFilter] = useState("");
  const [detail, setDetail] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/procurement/pos")
      .then(({ data }) => setPos(data.purchase_orders))
      .catch((e) => toast.error(apiError(e, "Couldn't load purchase orders")));
  }, []);
  useEffect(load, [load, reloadKey]);

  useEffect(() => {
    if (openPoId && pos) {
      const po = pos.find((p) => p.id === openPoId);
      if (po) setDetail(po);
      onOpenedPo();
    }
  }, [openPoId, pos, onOpenedPo]);

  const shown = (pos || []).filter((p) => {
    if (!filter) return true;
    if (filter === "open") return ["draft", "sent", "partially_received"].includes(p.status);
    if (filter === "overdue") return p.overdue;
    return p.status === filter;
  });

  const refreshDetail = async () => {
    load();
    if (detail) {
      try {
        const { data } = await api.get(`/admin/procurement/pos/${detail.id}`);
        setDetail(data);
      } catch { setDetail(null); }
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-2">
        {PO_FILTERS.map((f) => (
          <button key={f.id} onClick={() => setFilter(f.id)} data-testid={`pos-filter-${f.id || "all"}`}
            className={`rounded-full px-4 py-2 text-xs font-bold transition-colors ${filter === f.id ? "bg-amber-500 text-black" : "border border-white/10 text-neutral-400 hover:text-white"}`}>
            {f.label}
          </button>
        ))}
        <button onClick={onNewPo} data-testid="pos-new-btn"
          className="ml-auto flex h-10 items-center gap-2 rounded-full bg-amber-500 px-5 text-xs font-bold text-black transition-colors hover:bg-amber-400">
          <Plus className="h-4 w-4" /> New PO
        </button>
      </div>

      {!pos ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-neutral-600" /></div>
      ) : shown.length === 0 ? (
        <div className="rounded-2xl border border-white/[0.07] bg-white/[0.02] py-16 text-center text-sm text-neutral-500" data-testid="pos-empty">
          No purchase orders here yet.
        </div>
      ) : (
        <Fragment>
          <div className="hidden overflow-x-auto rounded-2xl border border-white/[0.07] md:block">
            <table className="w-full min-w-[860px] text-left text-sm" data-testid="pos-table">
              <thead>
                <tr className="border-b border-white/[0.07] bg-white/[0.03] text-[11px] uppercase tracking-wider text-neutral-500">
                  <th className="w-8 px-3 py-3"></th><th className="px-4 py-3">PO #</th><th className="px-4 py-3">Supplier</th>
                  <th className="px-4 py-3 text-right">Items</th><th className="px-4 py-3 text-right">Total</th>
                  <th className="px-4 py-3">Expected</th><th className="px-4 py-3 text-right">Received</th>
                  <th className="px-4 py-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((p) => (
                  <tr key={p.id} onClick={() => setDetail(p)}
                    className="cursor-pointer border-b border-white/[0.05] hover:bg-white/[0.02]" data-testid={`pos-row-${p.id}`}>
                    <td className="px-3 py-3 text-neutral-500"><ChevronRight className="h-4 w-4" /></td>
                    <td className="px-4 py-3 font-mono text-xs font-bold text-amber-400">{p.po_number}</td>
                    <td className="px-4 py-3 text-neutral-300">{p.supplier_name}</td>
                    <td className="px-4 py-3 text-right text-neutral-400">{p.items.length}</td>
                    <td className="px-4 py-3 text-right font-bold text-white">{fmt(p.total)}</td>
                    <td className={`px-4 py-3 text-xs ${p.overdue ? "font-bold text-red-400" : "text-neutral-500"}`} data-testid={`pos-expected-${p.id}`}>
                      {p.expected_delivery_date || "—"}{p.overdue ? " · overdue" : ""}
                    </td>
                    <td className="px-4 py-3 text-right text-xs text-neutral-400">{p.units_received}/{p.units_total}</td>
                    <td className="px-4 py-3"><StatusPill status={p.status} testid={`pos-status-${p.id}`} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="space-y-3 md:hidden" data-testid="pos-cards">
            {shown.map((p) => (
              <button key={p.id} onClick={() => setDetail(p)}
                className="w-full rounded-2xl border border-white/[0.07] bg-white/[0.02] p-4 text-left" data-testid={`pos-card-${p.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="font-mono text-sm font-bold text-amber-400">{p.po_number}</p>
                    <p className="text-xs text-neutral-400">{p.supplier_name} · {p.items.length} item(s)</p>
                  </div>
                  <StatusPill status={p.status} testid={`pos-card-status-${p.id}`} />
                </div>
                <p className="mt-2 text-xs text-neutral-500">
                  {fmt(p.total)} · {p.units_received}/{p.units_total} received
                  {p.expected_delivery_date ? ` · expected ${p.expected_delivery_date}` : ""}
                  {p.overdue ? " · " : ""}
                  {p.overdue && <span className="font-bold text-red-400">overdue</span>}
                </p>
              </button>
            ))}
          </div>
        </Fragment>
      )}

      {detail && (
        <PoDetailModal po={detail} onClose={() => setDetail(null)} onChanged={refreshDetail} />
      )}
    </div>
  );
}

/* ---------------- Page ---------------- */

export default function AdminProcurement() {
  const [tab, setTab] = useState("replenishment");
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [poPrefill, setPoPrefill] = useState(null);   // {source, supplier_id, items}
  const [poReload, setPoReload] = useState(0);
  const [openPoId, setOpenPoId] = useState(null);

  useEffect(() => {
    Promise.all([api.get("/admin/suppliers"), api.get("/admin/products")])
      .then(([s, pr]) => {
        setSuppliers(s.data.suppliers.filter((x) => x.active));
        setProducts(pr.data.filter((x) => x.active && !x.is_deleted));
      })
      .catch(() => {});
  }, []);

  const createFromRow = (r) => setPoPrefill({
    source: "recommendation", supplier_id: r.preferred_supplier_id || "",
    items: [{ product_id: r.product_id, sku: r.sku, product_name: r.product_name,
              variant_label: r.variant_label, qty: r.suggested_qty || r.reorder_qty || 1,
              unit_cost: r.latest_cost?.unit_cost ?? "", gst_rate: 18 }],
  });

  return (
    <div data-testid="admin-procurement" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-white">
            <PackagePlus className="h-6 w-6 text-amber-400" /> Procurement
          </h1>
          <p className="mt-1 text-sm text-neutral-500">
            Smart stock replenishment — low-stock alerts, supplier purchase orders and receiving.
          </p>
        </div>
        <div className="flex gap-2 rounded-full border border-white/[0.07] bg-[#FFFEFA] p-1">
          {[["replenishment", "Replenishment"], ["pos", "Purchase Orders"]].map(([id, label]) => (
            <button key={id} onClick={() => setTab(id)} data-testid={`proc-tab-${id}`}
              className={`rounded-full px-5 py-2.5 text-xs font-bold transition-colors ${tab === id ? "bg-amber-500 text-black" : "text-neutral-400 hover:text-white"}`}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {tab === "replenishment" ? (
        <ReplenishmentTab suppliers={suppliers} onCreatePo={createFromRow}
          onOpenPo={(po) => { setOpenPoId(po.po_id); setTab("pos"); }} />
      ) : (
        <PurchaseOrdersTab reloadKey={poReload} openPoId={openPoId} onOpenedPo={() => setOpenPoId(null)}
          onNewPo={() => setPoPrefill({ source: "manual", supplier_id: "",
            items: [{ product_id: "", sku: "", qty: 1, unit_cost: "", gst_rate: 18 }] })} />
      )}

      {poPrefill && (
        <PoFormModal suppliers={suppliers} products={products} prefill={poPrefill}
          onClose={() => setPoPrefill(null)}
          onCreated={() => { setPoPrefill(null); setPoReload((k) => k + 1); setTab("pos"); }} />
      )}
    </div>
  );
}
